import type { PropsWithChildren } from 'react';

import { Divider, Header, SubText } from '@procurify/ui'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate/FormattedShortDate'
import { useProcurifyIntl } from '@webapp/hooks'
import { type OrderItem } from '@webapp/models'
import { type PunchOutStatus, PunchOutStatuses } from '@webapp/models/enums'
import messages from './messages'
import { PunchOutOrderInfoStyled } from './styles/PunchOutOrderInfoStyled'

export interface IPunchOutOrderInfoProps {
  confirmationStatus: PunchOutStatus
  submittedDate: string
  confirmationStatusDescription?: string
  isFailedPunchoutOrder?: OrderItem['punchout_is_retriable']
}

export const PunchOutOrderInfo = ({
  confirmationStatus,
  submittedDate,
  confirmationStatusDescription,
  isFailedPunchoutOrder,
}: PropsWithChildren<IPunchOutOrderInfoProps>) => {
  const intl = useProcurifyIntl()

  const infoHeaderByStatus = {
    [PunchOutStatuses.ORDER_CANCELLED]: intl.formatMessage(
      messages.orderCancelled
    ),
    [PunchOutStatuses.PARTIALLY_PURCHASED]: intl.formatMessage(
      messages.partiallyPurchased
    ),
    [PunchOutStatuses.BACKORDERED]: intl.formatMessage(messages.backordered),
  }

  const infoPromptByStatus = {
    [PunchOutStatuses.BACKORDERED]: intl.formatMessage(
      messages.backorderedPrompt
    ),
  }

  const statusHeader = () => {
    if (isFailedPunchoutOrder)
      return intl.formatMessage(messages.failedOrderTitle)

    return (
      infoHeaderByStatus[confirmationStatus] ??
      intl.formatMessage(messages.defaultTitle)
    )
  }

  const statusPrompt = () => {
    if (isFailedPunchoutOrder)
      return intl.formatMessage(messages.failedOrderPrompt)

    return (
      infoPromptByStatus[confirmationStatus] ??
      intl.formatMessage(messages.defaultPrompt)
    )
  }

  return (
    <PunchOutOrderInfoStyled>
      <Header as='h5' color='danger'>
        {statusHeader()}
      </Header>
      <SubText>
        <FormattedShortDate date={submittedDate} />
      </SubText>
      <Divider />
      {confirmationStatusDescription && (
        <SubText>{confirmationStatusDescription}</SubText>
      )}
      <SubText>{statusPrompt()}</SubText>
    </PunchOutOrderInfoStyled>
  )
}

PunchOutOrderInfo.displayName = 'PunchOutOrderInfo'
